// ToolController - 操作コントローラー（道具レール／画面右側に常駐）
// もどす/やりなおす + タップ/けしごむ + ズーム
// 縦1列・大きめボタン・上アンカー（中央寄せにせず縦の空間を使う）

import { useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import UndoIcon from "@mui/icons-material/Undo";
import RedoIcon from "@mui/icons-material/Redo";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import ZoomOutIcon from "@mui/icons-material/ZoomOut";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import TouchAppOutlinedIcon from "@mui/icons-material/TouchAppOutlined";
import { ArcRotateCamera } from "@babylonjs/core/Cameras/arcRotateCamera";
import CenterFocusStrongIcon from "@mui/icons-material/CenterFocusStrong";
import { RootState } from "app/store";
import I18n from "common/i18n";
import { useScale, usePlaySound } from "common/utils";
import { actions } from "features/builder/slice";
import { useActionCommand } from "features/builder/hook";
import { useBuilderScene, useBuilderContext } from "features/builder/context";

const PANEL_BASE_WIDTH = 128;
const PANEL_BASE_HEIGHT = 784;
const PANEL_TOP = 160;

export const ToolController = () => {
  const play = usePlaySound();
  const dispatch = useDispatch();
  const scene = useBuilderScene();
  const { triggerReload } = useBuilderContext();
  const { scale, ratio } = useScale();
  const { undo, redo } = useActionCommand();
  const actionMenu = useSelector((state: RootState) => state.builder.actionMenu);
  const canUndoNow = useSelector((state: RootState) => state.builder.undoStacks.length > 0);
  const canRedoNow = useSelector((state: RootState) => state.builder.redoStacks.length > 0);

  const defaultRadius = useMemo(
    () => (ratio > 1 ? (scale > 0.7 ? 20 / scale : 40 * scale) : 20 / scale),
    [scale, ratio],
  );

  const handleUndo = () => {
    if (!canUndoNow) return;
    play();
    // Redux map を戻したあと 3D シーンを map state から再構築（AI生成・サイズ変更と同手法）
    if (undo()) triggerReload();
  };

  const handleRedo = () => {
    if (!canRedoNow) return;
    play();
    if (redo()) triggerReload();
  };

  const handleSelectTap = () => {
    play();
    dispatch(actions.updateActionMenu({ key: "tap", value: true, exclusive: true }));
    dispatch(actions.selectSlot(null));
    dispatch(actions.selectedAssetMesh(null));
  };

  const handleSelectEraser = () => {
    play();
    dispatch(actions.updateActionMenu({ key: "eraser", value: true, exclusive: true }));
    dispatch(actions.selectSlot(null));
    dispatch(actions.selectedAssetMesh(null));
  };

  const handleZoom = (zoom: number) => () => {
    if (!scene) return;
    const camera = scene.getCameraByName("camera") as ArcRotateCamera;
    if (camera) {
      play();
      if (zoom > 0) {
        camera.radius -= 1;
      } else if (zoom < 0) {
        camera.radius += 1;
      } else {
        camera.radius = defaultRadius;
        camera.alpha = Math.PI / 4;
        camera.beta = Math.PI / 4;
      }
    }
  };

  return (
    <div
      className="flex-col-el absolute right-0 z-[10000] pointer-events-none"
      style={{ top: PANEL_TOP * scale }}
    >
      <div
        style={{
          width: PANEL_BASE_WIDTH * scale,
          height: PANEL_BASE_HEIGHT * scale,
          marginRight: 8 * scale,
        }}
        className="pointer-events-auto"
      >
        <div
          style={{
            width: PANEL_BASE_WIDTH,
            height: PANEL_BASE_HEIGHT,
            transform: `scale(${scale})`,
          }}
          className="flex-col-el origin-top-left"
        >
          <div className="flex-col-el gap-3 w-full h-full p-3 box-border rounded-3xl bg-white/80 backdrop-blur-sm border-2 border-white shadow-lg">
            {/* もどす / やりなおす */}
            <div className="flex-col-el gap-2 w-full">
              <button
                onClick={handleUndo}
                disabled={!canUndoNow}
                className={`flex-col-el flex-center gap-1 w-full h-24 rounded-2xl border-0 type-inherit-font bg-gray-10 transition-all duration-150 ${
                  canUndoNow ? "active:scale-95 cursor-pointer" : "opacity-40 cursor-not-allowed"
                }`}
              >
                <span className="flex-col-el flex-center type-hero text-textcolor-black">
                  <UndoIcon fontSize="inherit" />
                </span>
                <span className="type-sm text-textcolor-black whitespace-nowrap">
                  {I18n.t("MSG_BUILDER_TOOL_UNDO")}
                </span>
              </button>
              <button
                onClick={handleRedo}
                disabled={!canRedoNow}
                className={`flex-col-el flex-center gap-1 w-full h-24 rounded-2xl border-0 type-inherit-font bg-gray-10 transition-all duration-150 ${
                  canRedoNow ? "active:scale-95 cursor-pointer" : "opacity-40 cursor-not-allowed"
                }`}
              >
                <span className="flex-col-el flex-center type-hero text-textcolor-black">
                  <RedoIcon fontSize="inherit" />
                </span>
                <span className="type-sm text-textcolor-black whitespace-nowrap">
                  {I18n.t("MSG_BUILDER_TOOL_REDO")}
                </span>
              </button>
            </div>

            {/* 区切り線 */}
            <div className="w-full border-t-2 border-gray-20" />

            {/* タップ / けしごむ */}
            <div className="flex-col-el gap-2 w-full">
              <button
                onClick={handleSelectTap}
                className={`flex-col-el flex-center gap-1 w-full h-24 rounded-2xl border-0 type-inherit-font transition-all duration-150 active:scale-95 cursor-pointer ${
                  actionMenu.tap ? "bg-blue-100" : "bg-blue-10"
                }`}
              >
                <span
                  className={`flex-col-el flex-center type-hero ${
                    actionMenu.tap ? "text-white" : "text-blue-100"
                  }`}
                >
                  <TouchAppOutlinedIcon fontSize="inherit" />
                </span>
                <span
                  className={`type-sm whitespace-nowrap ${
                    actionMenu.tap ? "text-white" : "text-blue-100"
                  }`}
                >
                  {I18n.t("MSG_BUILDER_TOOL_TAP")}
                </span>
              </button>
              <button
                onClick={handleSelectEraser}
                className={`flex-col-el flex-center gap-1 w-full h-24 rounded-2xl border-0 type-inherit-font transition-all duration-150 active:scale-95 cursor-pointer ${
                  actionMenu.eraser ? "bg-red-100" : "bg-red-10"
                }`}
              >
                <span
                  className={`flex-col-el flex-center type-hero ${
                    actionMenu.eraser ? "text-white" : "text-red-100"
                  }`}
                >
                  <DeleteOutlineIcon fontSize="inherit" />
                </span>
                <span
                  className={`type-sm whitespace-nowrap ${
                    actionMenu.eraser ? "text-white" : "text-red-100"
                  }`}
                >
                  {I18n.t("MSG_BUILDER_TOOL_ERASER")}
                </span>
              </button>
            </div>

            {/* 区切り線 */}
            <div className="w-full border-t-2 border-gray-20" />

            {/* ズーム（縦並び：ちいさく／おおきく／リセット） */}
            <div className="flex-col-el gap-2 w-full">
              <button
                onClick={handleZoom(-1)}
                className="flex-col-el flex-center gap-1 w-full h-24 rounded-2xl border-0 type-inherit-font bg-gray-10 hover:bg-gray-20 transition-all duration-150 active:scale-95 cursor-pointer"
              >
                <span className="flex-col-el flex-center type-hero text-textcolor-black">
                  <ZoomOutIcon fontSize="inherit" />
                </span>
                <span className="type-sm text-textcolor-black whitespace-nowrap">
                  {I18n.t("MSG_BUILDER_TOOL_ZOOM_OUT")}
                </span>
              </button>
              <button
                onClick={handleZoom(1)}
                className="flex-col-el flex-center gap-1 w-full h-24 rounded-2xl border-0 type-inherit-font bg-gray-10 hover:bg-gray-20 transition-all duration-150 active:scale-95 cursor-pointer"
              >
                <span className="flex-col-el flex-center type-hero text-textcolor-black">
                  <ZoomInIcon fontSize="inherit" />
                </span>
                <span className="type-sm text-textcolor-black whitespace-nowrap">
                  {I18n.t("MSG_BUILDER_TOOL_ZOOM_IN")}
                </span>
              </button>
              <button
                onClick={handleZoom(0)}
                className="flex-col-el flex-center gap-1 w-full h-24 rounded-2xl border-0 type-inherit-font bg-gray-10 hover:bg-gray-20 transition-all duration-150 active:scale-95 cursor-pointer"
              >
                <span className="flex-col-el flex-center type-hero text-textcolor-black">
                  <CenterFocusStrongIcon fontSize="inherit" />
                </span>
                <span className="type-sm text-textcolor-black whitespace-nowrap">
                  {I18n.t("MSG_BUILDER_TOOL_RESET")}
                </span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
